import { push } from "connected-react-router"
import { all, call, delay, put, select, take, fork, takeEvery, apply } from "redux-saga/effects"
import NProgress from 'nprogress'
import { ROUTE_OVERWATCH_ORG_PROFILE, ROUTE_OVERWATCH_USR_PROFILE } from "@/actions/routeOverwatch"
import authProvider, { OdAuthProvider } from "@/authProvider/authProvider"
import { UserResponseDto, UsersApi } from "@/gen/api/user"
import { organizationApiClient, userApiClient } from "@/lib/client"
import { CallReturnType } from "@/utils/sagaTypsHelper"
import { RegisterResourceAction, REGISTER_RESOURCE } from "ra-core"
import { clearLocationBookmark, readLocationBookmark } from "@/authProvider/locationBookmark"
import { ORGANIZATION_USERS } from "@/constants"
import { AccessTokenClaims } from "@/authProvider/odUserManager"
import { parseAccessToken } from "@/authProvider/jwtDecode"
import { ensureUserProfile } from "./ensureUserProfile"


export function handleUserProfileCompleteness(
  authProvider: OdAuthProvider,
  userApiClient: UsersApi,
  NProgress: { start: Function, done: Function },
) {
  return function* handleUserProfileCompleteness() {
    // spin up progress bar to show user redirection has began
    NProgress.start()
  
    yield delay(100)
    const ensureUserStored = authProvider.userManager.getUser.bind(authProvider.userManager)
    let n = 5
    let user: CallReturnType<typeof ensureUserStored> = null
    while (!user && n > 0) {
      user = yield call(ensureUserStored)
      yield delay(50)
      n = n - 1
    }
    
    let isLocalLogin = true
    try {
      const jwtAt = parseAccessToken<AccessTokenClaims>(user?.access_token || '').payload
      isLocalLogin = jwtAt.idp === 'local'
    } catch {
      isLocalLogin = true
    }
    
    try
    {
      const {ensureUserProfileSaga, userProfileDeferred} = ensureUserProfile()
      yield fork(ensureUserProfileSaga)
      const userProfile: UserResponseDto|undefined = yield userProfileDeferred
      
      if (isLocalLogin && userProfile && !userProfile.phone) {
        // user haven't finished profile edit
        yield put(push('/profile/create'))
      } else {
        const savedLocation = readLocationBookmark()
        clearLocationBookmark()
        if (savedLocation) {
          yield put(push(savedLocation))
        } else {
          yield put(push('/dashboard'))
        }
      }
    } catch (err) {
      yield put(push('/login'))
    }

    NProgress.done()
  }
}


export function handleOrganizationProfileCompleteness(
  authManager: OdAuthProvider,
  NProgress: { start: Function, done: Function },
) {

  return function* () {
    // spin up progress bar to show user redirection has began
    NProgress.start()

    try
    {
      const getPermissions: (params: any) => Promise<ReadonlyArray<string>> = authManager.getPermissions.bind(authManager)
      const permissions: CallReturnType<typeof getPermissions> = yield call<typeof getPermissions>(getPermissions, {})

      if (permissions.indexOf(ORGANIZATION_USERS) > -1) {
        const { data }: CallReturnType<typeof organizationApiClient.apiOrganizationsProfileGet> = 
          yield apply(organizationApiClient, 'apiOrganizationsProfileGet', [])
        
        if (data.name) { // org profile is already updated
          yield put(push('/dashboard'))
          
        } else {
          yield put(push('/settings/organization/profile/create'))
        }

      } else {
        yield put(push('/dashboard'))
      }

    } catch(err) {
      yield put(push('/dashboard'))
    }

    NProgress.done()
  }
} 


export default function* routeOverwatchSaga() {
  yield all([
    takeEvery(ROUTE_OVERWATCH_USR_PROFILE, handleUserProfileCompleteness(authProvider, userApiClient, NProgress)),
    takeEvery(ROUTE_OVERWATCH_ORG_PROFILE, handleOrganizationProfileCompleteness(authProvider, NProgress)),
  ])
}
